import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, isFirebaseConfigured } from "@/lib/firebase";
import { setScope } from "@/storage/session";
import { startSync, stopSync, flushNow } from "@/lib/sync";
import {
  loginUser,
  logoutUser,
  registerUser,
  resetPassword,
  resendVerification,
} from "@/storage/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Gates rendering until Firebase reports whether a session already exists,
  // so a returning user never sees the login screen flash before their app.
  const [initializing, setInitializing] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setInitializing(false);
      return;
    }

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        stopSync();
        setScope(null);
        setUser(null);
        setSyncStatus(null);
        setInitializing(false);
        return;
      }

      // Scope must be set before the sync layer touches localStorage, and both
      // must finish before AppProvider mounts and reads the user's routine.
      setScope(firebaseUser.uid, firebaseUser.email);
      const status = await startSync(firebaseUser.uid, firebaseUser.email, {
        onSyncError: setSyncError,
      });

      setSyncStatus(status);
      setUser(firebaseUser);
      setInitializing(false);
    });
  }, []);

  // The debounce window can swallow the last edit when a tab is closed or a
  // phone is locked mid-workout.
  useEffect(() => {
    if (!user) return;
    const onHide = () => {
      if (document.visibilityState === "hidden") flushNow();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flushNow);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flushNow);
    };
  }, [user]);

  const login = useCallback((email, password) => loginUser(email, password), []);
  const register = useCallback((email, password) => registerUser(email, password), []);

  const logout = useCallback(async () => {
    await flushNow();
    await logoutUser();
  }, []);

  const value = {
    user,
    initializing,
    syncStatus,
    syncError,
    dismissSyncError: () => setSyncError(null),
    isFirebaseConfigured,
    login,
    register,
    logout,
    resetPassword,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
